import { useCallback, useEffect, useState } from "react";

/**
 * Drives inline-edit state (click-to-edit, Enter/Escape, trim-and-no-op) for a single string field.
 *
 * - `editValue` resyncs from `value` only while not editing, so external updates don't clobber an in-progress edit.
 * - `commitEdit` no-ops (and reverts `editValue`) when the trimmed value is empty or unchanged from `value`.
 */
export function useInlineEdit(value: string, onSave: (next: string) => void | Promise<void>) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setEditValue(value);
    }
  }, [value, isEditing]);

  const startEditing = useCallback(() => {
    setEditValue(value);
    setIsEditing(true);
  }, [value]);

  const commitEdit = useCallback(() => {
    setIsEditing(false);
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === value) {
      setEditValue(value);
      return;
    }
    const result = onSave(trimmed);
    if (result instanceof Promise) {
      setIsSaving(true);
      // `.finally()` clears isSaving on both outcomes; the trailing no-op `.catch()` only
      // prevents an unhandled-rejection warning on the *derived* promise `.finally()`
      // returns — callers are expected to already handle their own save errors internally.
      result.finally(() => setIsSaving(false)).catch(() => {});
    }
  }, [editValue, value, onSave]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditValue(value);
  }, [value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") commitEdit();
      else if (e.key === "Escape") cancelEdit();
    },
    [commitEdit, cancelEdit]
  );

  return {
    isEditing,
    editValue,
    setEditValue,
    isSaving,
    startEditing,
    commitEdit,
    cancelEdit,
    handleKeyDown,
  };
}
